import { useEffect, useState, useSyncExternalStore } from 'react'

import type { Api, PackListed } from '~/api/endpoints'
import { useApi } from '~/data/data-context'

/**
 * Where what a food search finds comes from, and which of those it is narrowed to — the
 * nutrition spec, Logging an entry. The account's own, and each pack the library is copied into,
 * named by the pack's short name. Every one is on until turned off, and what is off is kept on
 * the device.
 */

export interface Repository {
  readonly id: string
  readonly label: string
}

/** The account's own foods, recipes and meals, among the repositories. */
export const MINE = 'mine'

/**
 * What a pack is called on a chip: its short name, or — on a manifest built before packs had
 * one — what can be told of it, so a chip is never a sentence.
 */
export function labelOf(pack: PackListed): string {
  if (pack.label !== undefined && pack.label.trim() !== '') {
    return pack.label
  }
  if (pack.source === 'usda') {
    return 'USDA'
  }
  try {
    const region = new Intl.DisplayNames(['en'], { type: 'region' }).of(pack.country.toUpperCase())
    if (region !== undefined && region !== '') {
      return region
    }
  } catch {
    // A country no name can be found for is called what the manifest calls it.
  }
  return pack.name
}

/** The manifest, asked once per server a page talks to rather than once per search. */
const manifests = new WeakMap<Api, Promise<PackListed[]>>()

/**
 * Every repository a search can be narrowed to, the account's own first and the packs in the
 * order the manifest names them. Null until the manifest has answered, and the account's own
 * alone where it cannot: a search is then narrowed by nothing.
 */
export function useFoodRepositories(): readonly Repository[] | null {
  const api = useApi()
  const [held, setHeld] = useState<readonly Repository[] | null>(null)
  useEffect(() => {
    let alive = true
    let asking = manifests.get(api)
    if (asking === undefined) {
      asking = api.packs()
      manifests.set(api, asking)
      // A manifest that could not be read is asked again next time, not remembered as none.
      asking.catch(() => {
        manifests.delete(api)
      })
    }
    asking.then(
      (packs) => {
        if (alive) {
          setHeld([
            { id: MINE, label: 'Mine' },
            ...[...packs]
              .sort((a, b) => sourceOrder(a) - sourceOrder(b))
              .map((pack) => ({ id: pack.id, label: labelOf(pack) })),
          ])
        }
      },
      () => {
        if (alive) setHeld([{ id: MINE, label: 'Mine' }])
      },
    )
    return () => {
      alive = false
    }
  }, [api])
  return held
}

/** The generic foods before the products, the way a search offers them. */
function sourceOrder(pack: PackListed): number {
  return pack.source === 'usda' ? 0 : 1
}

const KEY = 'gymcloud.food-search-off'

function read(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(KEY)
    const parsed: unknown = raw === null ? [] : JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [])
  } catch {
    return new Set()
  }
}

let off: ReadonlySet<string> = read()
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The repositories turned off on this device. */
export function useRepositoriesOff(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, () => off)
}

/**
 * One repository turned on or off, of [all]. The last one on stays on: a search narrowed to
 * nothing is not a search.
 */
export function turnRepository(id: string, on: boolean, all: readonly Repository[]): void {
  const next = new Set(off)
  if (on) {
    next.delete(id)
  } else {
    if (all.every((each) => each.id === id || next.has(each.id))) {
      return
    }
    next.add(id)
  }
  off = next
  try {
    localStorage.setItem(KEY, JSON.stringify([...next]))
  } catch {
    // A full or blocked store loses persistence, not the session's own choice.
  }
  for (const listener of listeners) {
    listener()
  }
}

/** Back to every repository on — what a fresh device holds, and what a test starts from. */
export function forgetRepositoriesOff(): void {
  off = new Set()
  for (const listener of listeners) {
    listener()
  }
}

/**
 * What a search is narrowed to, as the repositories that are on — null where none of [all] is
 * off, which narrows it by nothing.
 */
export function narrowingOf(
  all: readonly Repository[],
  held: ReadonlySet<string>,
): string[] | null {
  const on = all.filter((each) => !held.has(each.id)).map((each) => each.id)
  return on.length === all.length ? null : on
}
